import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  arrayUnion,
  arrayRemove,
  increment,
  query,
  orderBy,
  where,
  serverTimestamp,
} from '@/lib/firestore'
import { db, functions } from '@/config/firebase'
import { httpsCallable } from 'firebase/functions'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import type { Document, DocumentEntityType, DocumentType, DocumentShareMode } from '@/types'
import type { DocumentsState } from './state'

export interface CreateDocumentData {
  entityType: DocumentEntityType
  entityId: string
  title: string
  type: DocumentType
  content: string
  isPublic?: boolean
}

export interface UpdateDocumentData {
  title?: string
  type?: DocumentType
  content?: string
  isPublic?: boolean
}

export interface UpdateShareSettingsData {
  shareMode: DocumentShareMode
  sharedWithEmails?: string[]
  mfaRequiredEmails?: string[]
  allowExternalComments?: boolean
}

const mapDocData = (docSnap: { id: string; data: () => Record<string, unknown> }): Document => {
  const data = docSnap.data()
  return {
    id: docSnap.id,
    tenantId: data.tenantId,
    entityType: data.entityType,
    entityId: data.entityId,
    title: data.title,
    type: data.type,
    content: data.content,
    version: data.version || 1,
    isPublic: data.isPublic ?? true,
    shareMode: data.shareMode || 'none',
    shareToken: data.shareToken || undefined,
    sharedWithEmails: data.sharedWithEmails || undefined,
    mfaRequiredEmails: data.mfaRequiredEmails || undefined,
    allowExternalComments: data.allowExternalComments || false,
    reactions: (data.reactions as Record<string, string[]>) || undefined,
    viewCount: (data.viewCount as number) || 0,
    copilotSummary: data.copilotSummary ? {
      content: (data.copilotSummary as Record<string, unknown>).content as string,
      generatedAt: ((data.copilotSummary as Record<string, unknown>).generatedAt as { toDate?: () => Date })?.toDate?.() || new Date(),
      generatedByVersion: (data.copilotSummary as Record<string, unknown>).generatedByVersion as number,
      isStale: (data.copilotSummary as Record<string, unknown>).isStale as boolean,
    } : undefined,
    createdBy: data.createdBy || '',
    createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    updatedAt: (data.updatedAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    updatedBy: data.updatedBy || '',
  } as Document
}

export const createActions = (state: DocumentsState) => {
  const { logCreate, logUpdate, logDelete } = useActivityLog()

  const fetchDocuments = async (entityType: DocumentEntityType, entityId: string): Promise<void> => {
    state.isLoading = true
    state.error = null
    state.currentEntityType = entityType
    state.currentEntityId = entityId

    try {
      const authStore = useAuthStore()
      const isAdmin = authStore.user?.role === 'admin' || authStore.user?.role === 'operator'
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      const q = query(
        collection(db, 'documents'),
        ...baseConstraints,
        where('entityType', '==', entityType),
        where('entityId', '==', entityId),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)

      let documents = snapshot.docs.map(mapDocData)

      // Filter out internal documents for non-admin users
      if (!isAdmin) {
        documents = documents.filter((d) => d.isPublic)
      }

      state.documents = documents
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching documents'
      console.error('Error fetching documents:', error)
    } finally {
      state.isLoading = false
    }
  }

  const fetchDocumentById = async (id: string): Promise<Document | null> => {
    try {
      const docRef = doc(db, 'documents', id)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      return mapDocData({ id: docSnap.id, data: () => docSnap.data() as Record<string, unknown> })
    } catch (error) {
      console.error('Error fetching document:', error)
      return null
    }
  }

  const fetchPublicDocument = async (token: string): Promise<Document | null> => {
    state.isLoading = true
    state.error = null

    try {
      // No tenant constraint: public documents are accessed without authentication
      // shareMode constraint required so Firestore rules can validate the query
      const q = query(
        collection(db, 'documents'),
        where('shareToken', '==', token),
        where('shareMode', 'in', ['public', 'restricted'])
      )

      const snapshot = await getDocs(q)

      if (snapshot.empty) {
        state.publicDocument = null
        return null
      }

      const docSnap = snapshot.docs[0]
      const document = mapDocData(docSnap)

      state.publicDocument = document
      return document
    } catch (error) {
      console.error('Error fetching public document:', error)
      state.error = 'Error al cargar el documento'
      state.publicDocument = null
      return null
    } finally {
      state.isLoading = false
    }
  }

  const verifyEmailAccess = (email: string): boolean => {
    if (!state.publicDocument) return false
    if (state.publicDocument.shareMode === 'public') return true
    if (state.publicDocument.shareMode === 'restricted') {
      const allowed = state.publicDocument.sharedWithEmails || []
      const found = allowed.some((e) => e.toLowerCase() === email.toLowerCase())
      if (found) {
        state.publicAccessEmail = email
      }
      return found
    }
    return false
  }

  const createDocument = async (data: CreateDocumentData): Promise<string> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''

    const documentData = {
      ...getTenantData(),
      entityType: data.entityType,
      entityId: data.entityId,
      title: data.title,
      type: data.type,
      content: data.content,
      version: 1,
      isPublic: data.isPublic ?? true,
      shareMode: 'none' as DocumentShareMode,
      allowExternalComments: false,
      createdBy: userId,
      updatedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, 'documents'), documentData)

    const newDocument: Document = {
      id: docRef.id,
      entityType: data.entityType,
      entityId: data.entityId,
      title: data.title,
      type: data.type,
      content: data.content,
      version: 1,
      isPublic: data.isPublic ?? true,
      shareMode: 'none',
      allowExternalComments: false,
      createdBy: userId,
      updatedBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    state.documents.unshift(newDocument)

    // Log activity
    await logCreate('document', docRef.id, data.title)

    return docRef.id
  }

  const updateDocument = async (id: string, data: UpdateDocumentData): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'documents', id)

    const existingDoc = state.documents.find((d) => d.id === id)
    const newVersion = (existingDoc?.version || 0) + 1

    const updateData: Record<string, unknown> = {
      updatedBy: userId,
      updatedAt: serverTimestamp(),
      version: newVersion,
    }

    if (data.title !== undefined) updateData.title = data.title
    if (data.type !== undefined) updateData.type = data.type
    if (data.content !== undefined) {
      updateData.content = data.content
      // Mark copilot summary as stale if content changed
      if (existingDoc?.copilotSummary && data.content !== existingDoc.content) {
        updateData['copilotSummary.isStale'] = true
      }
    }
    if (data.isPublic !== undefined) updateData.isPublic = data.isPublic

    await updateDoc(docRef, updateData)

    const index = state.documents.findIndex((d) => d.id === id)
    if (index !== -1) {
      const updated = {
        ...state.documents[index],
        ...data,
        version: newVersion,
        updatedBy: userId,
        updatedAt: new Date(),
      }
      // Mark summary as stale locally
      if (data.content !== undefined && data.content !== existingDoc?.content && updated.copilotSummary) {
        updated.copilotSummary = { ...updated.copilotSummary, isStale: true }
      }
      state.documents[index] = updated
    }

    if (existingDoc) {
      await logUpdate('document', id, [], existingDoc.title)
    }
  }

  const updateShareSettings = async (id: string, settings: UpdateShareSettingsData): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'documents', id)
    const existingDoc = state.documents.find((d) => d.id === id)

    const updateData: Record<string, unknown> = {
      shareMode: settings.shareMode,
      allowExternalComments: settings.shareMode !== 'none' ? (settings.allowExternalComments ?? false) : false,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    if (settings.shareMode === 'none') {
      // Only delete fields that actually exist to avoid Firestore issues
      if (existingDoc?.shareToken) {
        updateData.shareToken = deleteField()
      }
      if (existingDoc?.sharedWithEmails) {
        updateData.sharedWithEmails = deleteField()
      }
      if (existingDoc?.mfaRequiredEmails) {
        updateData.mfaRequiredEmails = deleteField()
      }
    } else {
      // Generate token if it doesn't exist or mode was previously none
      if (!existingDoc?.shareToken || existingDoc.shareMode === 'none') {
        updateData.shareToken = crypto.randomUUID()
      }
      if (settings.shareMode === 'restricted') {
        updateData.sharedWithEmails = settings.sharedWithEmails || []
        updateData.mfaRequiredEmails = settings.mfaRequiredEmails || []
      } else {
        if (existingDoc?.sharedWithEmails) {
          updateData.sharedWithEmails = deleteField()
        }
        if (existingDoc?.mfaRequiredEmails) {
          updateData.mfaRequiredEmails = deleteField()
        }
      }
    }

    await updateDoc(docRef, updateData)

    // Update local state
    const newShareToken = settings.shareMode === 'none'
      ? undefined
      : (typeof updateData.shareToken === 'string' ? updateData.shareToken : existingDoc?.shareToken)

    const index = state.documents.findIndex((d) => d.id === id)
    if (index !== -1) {
      state.documents[index] = {
        ...state.documents[index],
        shareMode: settings.shareMode,
        shareToken: newShareToken,
        sharedWithEmails: settings.shareMode === 'restricted'
          ? settings.sharedWithEmails
          : undefined,
        mfaRequiredEmails: settings.shareMode === 'restricted'
          ? settings.mfaRequiredEmails
          : undefined,
        allowExternalComments: settings.shareMode !== 'none'
          ? (settings.allowExternalComments ?? false)
          : false,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }
  }

  const deleteDocument = async (id: string): Promise<void> => {
    const document = state.documents.find((d) => d.id === id)

    await deleteDoc(doc(db, 'documents', id))

    state.documents = state.documents.filter((d) => d.id !== id)

    if (document) {
      await logDelete('document', id, document.title)
    }
  }

  const clearDocuments = (): void => {
    state.documents = []
    state.currentEntityType = null
    state.currentEntityId = null
  }

  const clearPublicDocument = (): void => {
    state.publicDocument = null
    state.publicAccessEmail = null
  }

  const getDocumentById = (id: string): Document | undefined => {
    return state.documents.find((d) => d.id === id)
  }

  const toggleReaction = async (documentId: string, emoji: string, actorId: string): Promise<void> => {
    const docRef = doc(db, 'documents', documentId)
    const currentDoc = state.documents.find(d => d.id === documentId) || state.publicDocument
    const currentReactors = currentDoc?.reactions?.[emoji] || []
    const hasReacted = currentReactors.includes(actorId)
    const fieldPath = `reactions.${emoji}`

    if (hasReacted) {
      await updateDoc(docRef, { [fieldPath]: arrayRemove(actorId) })
    } else {
      await updateDoc(docRef, { [fieldPath]: arrayUnion(actorId) })
    }

    // Optimistic local update
    const updateLocal = (d: Document) => {
      const reactions = { ...(d.reactions || {}) }
      if (hasReacted) {
        reactions[emoji] = (reactions[emoji] || []).filter(id => id !== actorId)
        if (reactions[emoji].length === 0) delete reactions[emoji]
      } else {
        reactions[emoji] = [...(reactions[emoji] || []), actorId]
      }
      return { ...d, reactions }
    }

    const idx = state.documents.findIndex(d => d.id === documentId)
    if (idx !== -1) state.documents[idx] = updateLocal(state.documents[idx])
    if (state.publicDocument?.id === documentId) state.publicDocument = updateLocal(state.publicDocument)
  }

  const incrementViewCount = async (documentId: string): Promise<void> => {
    const storageKey = `doc-viewed-${documentId}`
    try {
      if (sessionStorage.getItem(storageKey)) return
      sessionStorage.setItem(storageKey, '1')
    } catch { /* ignore */ }

    try {
      const docRef = doc(db, 'documents', documentId)
      await updateDoc(docRef, { viewCount: increment(1) })

      // Optimistic local update
      const idx = state.documents.findIndex(d => d.id === documentId)
      if (idx !== -1) {
        state.documents[idx] = { ...state.documents[idx], viewCount: (state.documents[idx].viewCount || 0) + 1 }
      }
      if (state.publicDocument?.id === documentId) {
        state.publicDocument = { ...state.publicDocument, viewCount: (state.publicDocument.viewCount || 0) + 1 }
      }
    } catch { /* ignore for public/unauthenticated visitors */ }
  }

  const searchDocuments = async (
    searchQuery?: string,
    entityType?: DocumentEntityType,
  ): Promise<Document[]> => {
    try {
      const authStore = useAuthStore()
      const isAdmin = authStore.user?.role === 'admin' || authStore.user?.role === 'operator'
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      const filters = [...baseConstraints]

      if (entityType) {
        filters.push(where('entityType', '==', entityType))
      }

      const q = query(
        collection(db, 'documents'),
        ...filters,
        orderBy('updatedAt', 'desc'),
      )

      const snapshot = await getDocs(q)
      let documents = snapshot.docs.map(mapDocData)

      if (!isAdmin) {
        documents = documents.filter((d) => d.isPublic)
      }

      if (searchQuery) {
        const lower = searchQuery.toLowerCase()
        documents = documents.filter((d) => d.title.toLowerCase().includes(lower))
      }

      return documents.slice(0, 50)
    } catch (error) {
      console.error('Error searching documents:', error)
      return []
    }
  }

  const generateSummary = async (documentId: string): Promise<void> => {
    const generateFn = httpsCallable<{ documentId: string }, { success: boolean; summary: Document['copilotSummary'] }>(
      functions,
      'generateDocumentSummary'
    )
    const result = await generateFn({ documentId })

    if (result.data.success && result.data.summary) {
      const summary = {
        ...result.data.summary,
        generatedAt: new Date(),
        isStale: false,
      }
      // Update local state
      const idx = state.documents.findIndex((d) => d.id === documentId)
      if (idx !== -1) {
        state.documents[idx] = { ...state.documents[idx], copilotSummary: summary }
      }
      if (state.publicDocument?.id === documentId) {
        state.publicDocument = { ...state.publicDocument, copilotSummary: summary }
      }
    }
  }

  const confirmSummary = async (documentId: string): Promise<void> => {
    const docRef = doc(db, 'documents', documentId)
    const existingDoc = state.documents.find((d) => d.id === documentId)
    if (!existingDoc?.copilotSummary) return

    await updateDoc(docRef, {
      'copilotSummary.isStale': false,
      'copilotSummary.generatedByVersion': existingDoc.version,
    })

    const updated = {
      ...existingDoc.copilotSummary,
      isStale: false,
      generatedByVersion: existingDoc.version,
    }
    const idx = state.documents.findIndex((d) => d.id === documentId)
    if (idx !== -1) {
      state.documents[idx] = { ...state.documents[idx], copilotSummary: updated }
    }
  }

  return {
    fetchDocuments,
    fetchDocumentById,
    fetchPublicDocument,
    searchDocuments,
    verifyEmailAccess,
    createDocument,
    updateDocument,
    updateShareSettings,
    deleteDocument,
    clearDocuments,
    clearPublicDocument,
    getDocumentById,
    toggleReaction,
    incrementViewCount,
    generateSummary,
    confirmSummary,
  }
}
